'use client';

// Legacy: pro-frontend/src/app/portfoliostandard/portfoliostandard.component.ts
// Renders server HTML from `/pro/resume/public/v2` in a sandboxed iframe
// (same isolation approach as portfoliosettings ResumePreviewPanel).

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buildPublicExtraInfo,
  getPublicPortfolio,
  sendPublicHeartbeat,
} from '@/features/portfolio/api/portfolio';
import { unwrapResume } from '@/features/portfolio/lib/map-resume-to-portfolio';
import { getPrimaryToken, getTemporaryToken, setPrimaryToken, setTemporaryToken } from '@/lib/auth/storage';
import { apiClient } from '@/lib/api/client';

import {
  PREVIEW_FALLBACK_HEIGHT_PX,
  PREVIEW_HEIGHT_RETRY_MS,
  PREVIEW_WIDTH_PX,
  computePreviewScale,
  getPreviewRootElement,
  measurePreviewDocumentHeight,
} from '@/features/portfoliosettings/lib/preview-iframe-layout';

import { getResumeDisplayName, isExampleResumeEmail } from './lib/example-resume';
import { preparePublicResumeHtml } from './lib/prepare-public-resume-html';
import { PublicResumeSkeleton } from './PublicResumeSkeleton';

import './public-resume.css';

type PublicResumeClientProps = {
  id: string;
};

type TemporarySignupResponse = {
  status: string;
  token?: string;
  message?: string;
};

export function PublicResumeClient({ id }: PublicResumeClientProps) {
  const [html, setHtml] = useState('');
  const [name, setName] = useState('');
  const [isExample, setIsExample] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [iframeHeight, setIframeHeight] = useState(PREVIEW_FALLBACK_HEIGHT_PX);
  const [previewScale, setPreviewScale] = useState(1);
  const [ctaLoading, setCtaLoading] = useState(false);
  const visitorIdRef = useRef<number | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const frameWrapRef = useRef<HTMLDivElement | null>(null);
  const heightRetryTimeoutIdsRef = useRef<number[]>([]);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const sanitizedHtml = useMemo(() => (html ? preparePublicResumeHtml(html) : ''), [html]);

  const syncPreviewScale = useCallback(() => {
    const wrap = frameWrapRef.current;
    if (!wrap) {
      return;
    }
    setPreviewScale(computePreviewScale(wrap.clientWidth, 4));
  }, []);

  const syncIframeHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) {
      return;
    }
    try {
      setIframeHeight(measurePreviewDocumentHeight(iframe.contentDocument));
    } catch {
      // cross-origin / empty doc — keep fallback
    }
  }, []);

  const clearHeightRetryTimeouts = useCallback(() => {
    for (let i = 0; i < heightRetryTimeoutIdsRef.current.length; i++) {
      window.clearTimeout(heightRetryTimeoutIdsRef.current[i]);
    }
    heightRetryTimeoutIdsRef.current = [];
  }, []);

  const schedulePreviewHeightSync = useCallback(
    (doc: Document) => {
      syncIframeHeight();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          syncIframeHeight();
        });
      });

      clearHeightRetryTimeouts();
      for (let i = 0; i < PREVIEW_HEIGHT_RETRY_MS.length; i++) {
        const timeoutId = window.setTimeout(() => {
          syncIframeHeight();
        }, PREVIEW_HEIGHT_RETRY_MS[i]);
        heightRetryTimeoutIdsRef.current.push(timeoutId);
      }

      const images = doc.querySelectorAll('img');
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        if (img && !img.complete) {
          img.addEventListener('load', syncIframeHeight, { once: true });
          img.addEventListener('error', syncIframeHeight, { once: true });
        }
      }

      const fonts = doc.fonts;
      if (fonts && fonts.ready) {
        void fonts.ready.then(() => {
          syncIframeHeight();
        });
      }
    },
    [clearHeightRetryTimeouts, syncIframeHeight],
  );

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe && iframe.contentDocument;
    if (!doc) {
      return;
    }
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (typeof ResizeObserver !== 'undefined') {
      const root = getPreviewRootElement(doc);
      const observer = new ResizeObserver(() => {
        syncIframeHeight();
      });
      if (doc.body) {
        observer.observe(doc.body);
      }
      if (root && root !== doc.body) {
        observer.observe(root);
      }
      resizeObserverRef.current = observer;
    }
    schedulePreviewHeightSync(doc);
    syncPreviewScale();
  }, [schedulePreviewHeightSync, syncIframeHeight, syncPreviewScale]);

  useEffect(() => {
    const wrap = frameWrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') {
      syncPreviewScale();
      return;
    }
    const observer = new ResizeObserver(() => {
      syncPreviewScale();
    });
    observer.observe(wrap);
    syncPreviewScale();
    return () => {
      observer.disconnect();
    };
  }, [loading, syncPreviewScale]);

  useEffect(() => {
    return () => {
      clearHeightRetryTimeouts();
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [clearHeightRetryTimeouts]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const response = await getPublicPortfolio(id, buildPublicExtraInfo('resume'));
        if (cancelled) {
          return;
        }

        if (!response || response.status === 'error') {
          setErrorMessage(
            response?.message && String(response.message).length
              ? String(response.message)
              : 'This resume could not be found or is not available.',
          );
          return;
        }

        const resume = unwrapResume(response.resume);
        if (!resume) {
          setErrorMessage('This resume could not be found or is not available.');
          return;
        }

        const displayName = getResumeDisplayName(resume);
        setName(displayName);
        document.title = `View ${displayName}'s resume on KudosWall`;

        const email =
          typeof (resume as { email?: unknown }).email === 'string'
            ? (resume as { email?: string }).email
            : '';
        setIsExample(isExampleResumeEmail(email));

        if (response.html) {
          setHtml(String(response.html));
        } else {
          setErrorMessage('This resume could not be rendered right now. Please try again later.');
          return;
        }

        if (response.visitor_id) {
          visitorIdRef.current = response.visitor_id;
          heartbeatRef.current = setInterval(() => {
            if (visitorIdRef.current) {
              void sendPublicHeartbeat(visitorIdRef.current).catch(() => undefined);
            }
          }, 5000);
        }
      } catch {
        if (!cancelled) {
          setErrorMessage(
            'We could not load this resume. Please check your connection and try again.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [id]);

  const startBuilding = async () => {
    if (ctaLoading) {
      return;
    }
    const editorPath = '/account/portfoliosettings?mode=editor&action=import';
    if (getTemporaryToken() || getPrimaryToken()) {
      window.location.href = editorPath;
      return;
    }

    setCtaLoading(true);
    try {
      const { data } = await apiClient.post<TemporarySignupResponse>(
        '/pro/auth/temporary-signup',
        {},
        { timeout: 10000 },
      );
      if (data.status === 'success' && data.token) {
        setTemporaryToken(data.token);
        setPrimaryToken(data.token);
        window.location.href = editorPath;
        return;
      }
      setCtaLoading(false);
    } catch {
      setCtaLoading(false);
    }
  };

  if (loading) {
    return <PublicResumeSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="kw-public-resume-error" role="alert">
        <p>{errorMessage}</p>
        <p>
          <Link href="/">Back to home</Link>
        </p>
      </div>
    );
  }

  const scaleLayoutStyle = {
    transform: `scale(${previewScale})`,
    transformOrigin: 'top left',
    width: PREVIEW_WIDTH_PX,
    height: `${iframeHeight}px`,
  };

  const scaleClipStyle = {
    width: `${Math.max(1, PREVIEW_WIDTH_PX * previewScale)}px`,
    height: `${Math.max(1, iframeHeight * previewScale)}px`,
    overflow: 'hidden' as const,
    margin: '0 auto',
  };

  return (
    <div className="kw-public-resume">
      {isExample ? (
        <div className="kw-public-resume-overlay">
          <div className="kw-public-resume-overlay-content">
            <h2>Make a Resume Like This — Free &amp; No Signup Needed</h2>
            <button
              type="button"
              className="kw-public-resume-cta"
              onClick={() => void startBuilding()}
              disabled={ctaLoading}
            >
              {ctaLoading ? 'Starting…' : 'Start Building →'}
            </button>
          </div>
        </div>
      ) : null}

      <div ref={frameWrapRef} className="kw-public-resume-frame-wrap">
        <div className="kw-public-resume-scale-clip" style={scaleClipStyle}>
          <div className="kw-public-resume-scale" style={scaleLayoutStyle}>
            <iframe
              ref={iframeRef}
              className="kw-public-resume-frame"
              title={name ? `${name}'s resume` : 'Public resume'}
              srcDoc={sanitizedHtml}
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              onLoad={handleIframeLoad}
              style={{ height: iframeHeight, width: PREVIEW_WIDTH_PX }}
            />
          </div>
        </div>
      </div>

      {!isExample ? (
        <div className="kw-public-resume-watermark">
          <Link href="/">
            {/* eslint-disable-next-line @next/next/no-img-element -- static branding asset */}
            <img
              src="/assets/images/footer-watermark.png"
              alt="Made with KudosWall"
              height={100}
            />
          </Link>
        </div>
      ) : null}
    </div>
  );
}
